/**
 * Filesystem Log Transport
 *
 * Batches frontend logs and sends them to POST /api/debug/client-logs
 * handled by the Vite dev server plugin (frontend/vite.config.ts).
 *
 * Only sends selected debug traces to avoid noise.
 */

const LOG_ENDPOINT = '/api/debug/client-logs';
const BATCH_INTERVAL = 3000; // Flush every 3 seconds
const MAX_BATCH_SIZE = 50;

interface LogEntry {
  level: string;
  message: string;
  timestamp: string;
  logger_name: string | null;
  data: Record<string, unknown> | null;
}

let buffer: LogEntry[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;

// Only capture targeted debug traces.
const CAPTURE_PREFIXES = ['[PERF]', '[CMD_CENTER]', '[SCHEDULE_PERF]', '[PARSER_TRACE]'];

function shouldCapture(message: string): boolean {
  return CAPTURE_PREFIXES.some((prefix) => message.includes(prefix));
}

async function flush() {
  if (buffer.length === 0) return;

  const batch = buffer.splice(0, MAX_BATCH_SIZE);

  try {
    await fetch(LOG_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        logs: batch,
        page: window.location.pathname,
        session_id: sessionStorage.getItem('debug-session-id') || undefined,
      }),
    });
  } catch {
    // Silently fail — don't break the app for debug logging
  }
}

export function captureLog(
  level: string,
  loggerName: string | null,
  message: string,
  data?: Record<string, unknown>,
) {
  if (!shouldCapture(message)) return;

  buffer.push({
    level,
    message,
    timestamp: new Date().toISOString(),
    logger_name: loggerName,
    data: data || null,
  });

  if (buffer.length >= MAX_BATCH_SIZE) {
    flush();
  }
}

export function startLogTransport() {
  if (flushTimer) return;

  // Generate a session ID for correlating logs
  if (!sessionStorage.getItem('debug-session-id')) {
    sessionStorage.setItem(
      'debug-session-id',
      `fe-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    );
  }

  flushTimer = setInterval(flush, BATCH_INTERVAL);

  // Flush on page unload
  window.addEventListener('beforeunload', () => flush());
}

export function stopLogTransport() {
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  flush(); // Final flush
}
